import { useGeoStore } from '@/entities/geo/model';
import { Fallback } from '@/shared/ui';
import { Map, type MapProps } from '@vis.gl/react-maplibre';
import type { StyleSpecification } from 'maplibre-gl';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import React from 'react';
import styles from './MapLibreMap.module.scss';
import { MapLoader } from './MapLoader';

interface IMapLibreMapProps extends Partial<MapProps> {
	children?: React.ReactNode;
	is3DGraphics?: boolean;
	isBlockMove?: boolean;
	isLoading?: boolean;
}

const getSources = (nameCity: string): StyleSpecification['sources'] => ({
	building: {
		maxzoom: 16,
		minzoom: 12,
		tiles: [`https://tiles.foodriver.online:8443/${nameCity}/building/{z}/{x}/{y}?format=pbf`],
		type: 'vector',
	},
	landuse: {
		maxzoom: 16,
		minzoom: 10,
		tiles: [`https://tiles.foodriver.online:8443/${nameCity}/landuse/{z}/{x}/{y}?format=pbf`],
		type: 'vector',
	},
	place: {
		maxzoom: 16,
		minzoom: 6,
		tiles: [`https://tiles.foodriver.online:8443/${nameCity}/place/{z}/{x}/{y}?format=pbf`],
		type: 'vector',
	},
	poi: {
		maxzoom: 16,
		minzoom: 12,
		tiles: [`https://tiles.foodriver.online:8443/${nameCity}/poi/{z}/{x}/{y}?format=pbf`],
		type: 'vector',
	},
	transport_line: {
		maxzoom: 16,
		minzoom: 8,
		tiles: [`https://tiles.foodriver.online:8443/${nameCity}/transport_line/{z}/{x}/{y}?format=pbf`],
		type: 'vector',
	},
	water: {
		maxzoom: 16,
		minzoom: 6,
		tiles: [`https://tiles.foodriver.online:8443/${nameCity}/water/{z}/{x}/{y}?format=pbf`],
		type: 'vector',
	},
	water_line: {
		maxzoom: 16,
		minzoom: 8,
		tiles: [`https://tiles.foodriver.online:8443/${nameCity}/water_line/{z}/{x}/{y}?format=pbf`],
		type: 'vector',
	},
	water_polygon: {
		maxzoom: 16,
		minzoom: 6,
		tiles: [`https://tiles.foodriver.online:8443/${nameCity}/water_polygon/{z}/{x}/{y}?format=pbf`],
		type: 'vector',
	},
});

export const MAP_LAYERS: StyleSpecification['layers'] = [
	/* ================= BACKGROUND ================= */
	{
		id: 'background',
		paint: { 'background-color': '#101333' },
		type: 'background',
	},

	/* ================= WATER ================= */
	{
		id: 'water-polygon',
		paint: { 'fill-color': '#001b4d' },
		source: 'water_polygon',
		'source-layer': 'water_polygon',
		type: 'fill',
	},
	{
		id: 'water-line',
		paint: {
			'line-color': '#1a3f6d',
			'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 14, 1.5, 18, 3],
		},
		source: 'water_line',
		'source-layer': 'water_line',
		type: 'line',
	},
	{
		id: 'water-original',
		paint: { 'fill-color': '#001b4d' },
		source: 'water',
		'source-layer': 'water',
		type: 'fill',
	},

	/* ================= LANDUSE ================= */
	{
		id: 'landuse',
		paint: {
			'fill-color': [
				'match',
				['get', 'class'],
				'park',
				'#144a58',
				'forest',
				'#133948',
				'residential',
				'#101332',
				'industrial',
				'#23315f',
				'#1c2a41',
			],
		},
		source: 'landuse',
		'source-layer': 'landuse',
		type: 'fill',
	},
	{
		id: 'landuse-outline',
		paint: {
			'line-color': '#22345a',
			'line-width': 0.5,
		},
		source: 'landuse',
		'source-layer': 'landuse',
		type: 'line',
	},

	/* ================= BUILDINGS ================= */
	{
		id: 'buildings-3d',
		minzoom: 14,
		paint: {
			'fill-extrusion-base': ['coalesce', ['get', 'min_height'], 0],
			'fill-extrusion-color': '#313e5e',
			'fill-extrusion-height': ['coalesce', ['get', 'height'], 10],
			'fill-extrusion-opacity': 0.9,
		},
		source: 'building',
		'source-layer': 'building',
		type: 'fill-extrusion',
	},
	{
		id: 'buildings-2d',
		minzoom: 14,
		paint: {
			'fill-color': '#313e5e',
			'fill-opacity': 0.9,
		},
		source: 'building',
		'source-layer': 'building',
		type: 'fill',
	},
	{
		id: 'building-labels',
		layout: {
			'text-field': ['get', 'name'],
			'text-font': ['Open Sans Regular'],
			'text-size': 11,
		},
		minzoom: 16,
		paint: {
			'text-color': '#d0d6ff',
			'text-halo-color': '#1a1f3a',
			'text-halo-width': 1,
		},
		source: 'building',
		'source-layer': 'building',
		type: 'symbol',
	},

	/* ================= ROADS ================= */
	{
		filter: ['in', ['get', 'class'], ['literal', ['motorway', 'trunk', 'primary', 'secondary']]],
		id: 'roads-main',
		paint: {
			'line-color': '#153a7d',
			'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.8, 14, 4, 16, 7],
		},
		source: 'transport_line',
		'source-layer': 'transport_line',
		type: 'line',
	},
	{
		filter: ['in', ['get', 'subclass'], ['literal', ['residential', 'service', 'living_street', 'unclassified']]],
		id: 'roads-local',
		paint: {
			'line-color': '#304a7d',
			'line-width': ['interpolate', ['linear'], ['zoom'], 12, 0.5, 15, 2.5],
		},
		source: 'transport_line',
		'source-layer': 'transport_line',
		type: 'line',
	},

	/* ================= ROAD LABELS ================= */
	{
		id: 'road-labels',
		layout: {
			'symbol-placement': 'line',
			'symbol-spacing': 400,
			'text-field': ['get', 'name'],
			'text-font': ['Open Sans Regular'],
			'text-size': ['interpolate', ['linear'], ['zoom'], 14, 11, 17, 14],
		},
		minzoom: 14,
		paint: {
			'text-color': '#bdc1c7',
			'text-halo-color': '#191a2c',
			'text-halo-width': 1.5,
		},
		source: 'transport_line',
		'source-layer': 'transport_line',
		type: 'symbol',
	},

	{
		id: 'water-labels',
		layout: {
			'text-field': ['get', 'name'],
			'text-font': ['Open Sans Italic'],
			'text-size': ['interpolate', ['linear'], ['zoom'], 8, 11, 14, 14],
		},
		minzoom: 8,
		paint: {
			'text-color': '#7aa2ff',
			'text-halo-color': '#0d1028',
			'text-halo-width': 1.2,
		},
		source: 'water',
		'source-layer': 'water',
		type: 'symbol',
	},

	/* ================= PLACES ================= */
	{
		filter: ['in', ['get', 'class'], ['literal', ['city', 'town', 'village', 'neighbourhood']]],
		id: 'place-labels',
		layout: {
			'text-field': ['get', 'name'],
			'text-font': ['Open Sans Semibold'],
			'text-size': ['interpolate', ['linear'], ['zoom'], 6, 12, 10, 16, 14, 20],
		},
		minzoom: 6,
		paint: {
			'text-color': '#cce0fe',
			'text-halo-color': '#22283e',
			'text-halo-width': 2,
		},
		source: 'place',
		'source-layer': 'place',
		type: 'symbol',
	},
];

export const getMapStyle = (city: string, is3DGraphics?: boolean): StyleSpecification => {
	const layers = MAP_LAYERS.filter((layer) => {
		if (layer.id === 'buildings-3d') return is3DGraphics;

		if (layer.id === 'buildings-2d') return !is3DGraphics;

		return true;
	});

	return {
		glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
		layers,
		sources: getSources(city),
		version: 8,
	};
};

const MapLibreMap: React.FC<IMapLibreMapProps> = ({ children, ...props }) => {
	const { nameCity } = useGeoStore();

	if (!nameCity) return <Fallback />;

	const mapStyle = React.useMemo(() => getMapStyle(nameCity, props.is3DGraphics), [nameCity, props.is3DGraphics]);

	return (
		<div className={styles.container}>
			<Map
				mapLib={maplibregl}
				mapStyle={mapStyle}
				minZoom={props.minZoom ?? 14}
				maxZoom={props.maxZoom ?? 14}
				doubleClickZoom={false}
				scrollZoom={false}
				dragRotate={false}
				touchPitch={false}
				keyboard={false}
				dragPan={!props.isBlockMove}
				touchZoomRotate={false}
				attributionControl={false}
				pixelRatio={Math.max(window.devicePixelRatio)}
				initialViewState={{
					latitude: props.latitude ?? 0,
					longitude: props.longitude ?? 0,
					zoom: props.zoom ?? 14,
				}}
				style={{
					height: '100%',
					userSelect: 'none',
					visibility: props.isLoading ? 'hidden' : 'visible',
					width: '100%',
				}}
				{...props}
			>
				{children}
			</Map>

			{props.isLoading && <MapLoader />}
		</div>
	);
};

export default MapLibreMap;
